import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Buildings, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODS = [
  { key: "itsm", label: "ITSM" },
  { key: "soc", label: "SOC" },
];

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [newMods, setNewMods] = useState(["itsm"]);

  const load = () => api.get("/client-registry").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (row, mod) => {
    const has = (row.modules || []).includes(mod);
    const modules = has ? row.modules.filter(m => m !== mod) : [...row.modules, mod];
    if (modules.length === 0) { toast.error("A client must belong to at least one module"); return; }
    try { await api.patch(`/client-registry/${row.id}`, { modules }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const add = async () => {
    if (!name.trim()) { toast.error("Client name required"); return; }
    if (newMods.length === 0) { toast.error("Pick at least one module"); return; }
    try { await api.post("/client-registry", { name: name.trim(), modules: newMods }); setName(""); setNewMods(["itsm"]); load(); toast.success("Client added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name} from the client registry?`)) return;
    try { await api.delete(`/client-registry/${row.id}`); load(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleNewMod = (m) => setNewMods(newMods.includes(m) ? newMods.filter(x => x !== m) : [...newMods, m]);

  return (
    <div className="p-6 space-y-5 max-w-4xl" data-testid="clients-view">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Configuration</div>
        <h1 className="font-display text-3xl font-black flex items-center gap-2"><Buildings size={26} /> Clients</h1>
        <p className="text-sm text-muted-foreground mt-1">Tag each client for ITSM, SOC, or both. SOC screens only list clients tagged for SOC.</p>
      </div>

      <Card className="p-4 border-border rounded-sm flex flex-wrap items-end gap-3" data-testid="client-add-card">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-mono uppercase text-muted-foreground">New client name</label>
          <Input data-testid="client-name-input" value={name} onChange={e => setName(e.target.value)} className="rounded-sm h-9 text-sm mt-1" />
        </div>
        <div className="flex items-center gap-2">
          {MODS.map(m => (
            <button key={m.key} data-testid={`client-new-mod-${m.key}`} onClick={() => toggleNewMod(m.key)}
              className={`text-xs font-mono uppercase px-3 py-2 rounded-sm border ${newMods.includes(m.key) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{m.label}</button>
          ))}
        </div>
        <Button data-testid="client-add-btn" onClick={add} className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-1" /> Add</Button>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left px-4 py-2.5">Client</th><th className="text-left px-4 py-2.5">Modules</th><th className="px-4 py-2.5"></th></tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} data-testid={`client-row-${row.name}`} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{row.name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {MODS.map(m => (
                      <button key={m.key} data-testid={`client-toggle-${row.name}-${m.key}`} onClick={() => toggle(row, m.key)}
                        className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded-sm border ${(row.modules || []).includes(m.key) ? (m.key === "soc" ? "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40" : "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/40") : "border-border text-muted-foreground/50"}`}>{m.label}</button>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`client-delete-${row.name}`} onClick={() => remove(row)} className="text-muted-foreground hover:text-red-500"><Trash size={15} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-muted-foreground">No clients yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
